/**
 * Recruiting-Aufgaben (Schritt 2). Eigenes Modell — die Tabelle `tasks` des
 * Projektmoduls bleibt unberührt.
 */

const API_BASE = '/api/v1/recruiting-tasks';

export type TaskStatus = 'OPEN' | 'IN_PROGRESS' | 'DONE' | 'CANCELLED';
export type TaskPriority = 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT';
export type TaskType = 'CALL' | 'FOLLOW_UP' | 'REVIEW' | 'CLIENT_FEEDBACK' | 'INTERVIEW' | 'DOCUMENT' | 'GENERAL';

export const TASK_STATUS_LABEL: Record<TaskStatus, string> = {
  OPEN: 'Offen', IN_PROGRESS: 'In Arbeit', DONE: 'Erledigt', CANCELLED: 'Storniert',
};

export const TASK_PRIORITY_LABEL: Record<TaskPriority, { label: string; color: string }> = {
  LOW:    { label: 'Niedrig',  color: '#9CA3AF' },
  NORMAL: { label: 'Normal',   color: '#6B7280' },
  HIGH:   { label: 'Hoch',     color: '#F59E0B' },
  URGENT: { label: 'Dringend', color: '#EF4444' },
};

export const TASK_TYPE_LABEL: Record<TaskType, string> = {
  CALL: 'Anruf', FOLLOW_UP: 'Wiedervorlage', REVIEW: 'Prüfung',
  CLIENT_FEEDBACK: 'Kundenrückmeldung', INTERVIEW: 'Interview',
  DOCUMENT: 'Unterlagen', GENERAL: 'Allgemein',
};

export interface RecruitingTask {
  id: string;
  organization_id: string;
  title: string;
  description: string | null;
  candidate_id: string | null;
  company_id: string | null;
  contact_id: string | null;
  job_posting_id: string | null;
  submission_id: string | null;
  assigned_user_id: string;
  task_type: TaskType;
  priority: TaskPriority;
  status: TaskStatus;
  due_at: string | null;
  created_by_user_id: string | null;
  completed_by_user_id: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface TaskListe {
  items: RecruitingTask[];
  total: number;
  limit: number;
  offset: number;
}

export interface TaskFilter {
  candidate_id?: string;
  company_id?: string;
  submission_id?: string;
  assigned_user_id?: string;
  mine?: boolean;
  status?: TaskStatus[];
  open?: boolean;
  overdue?: boolean;
  due_today?: boolean;
  limit?: number;
  offset?: number;
}

async function fetchApi(url: string, options: RequestInit = {}) {
  const res = await fetch(url, {
    ...options,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...options.headers },
  });
  const json = await res.json();
  if (!res.ok || !json.success) {
    const fehler = new Error(json?.error || 'Anfrage fehlgeschlagen') as Error & { code?: string };
    fehler.code = json?.code;
    throw fehler;
  }
  return json;
}

const recruitingTasksService = {
  async liste(filter: TaskFilter = {}): Promise<TaskListe> {
    const qs = new URLSearchParams();
    if (filter.candidate_id) qs.set('candidate_id', filter.candidate_id);
    if (filter.company_id) qs.set('company_id', filter.company_id);
    if (filter.submission_id) qs.set('submission_id', filter.submission_id);
    if (filter.assigned_user_id) qs.set('assigned_user_id', filter.assigned_user_id);
    if (filter.mine) qs.set('mine', 'true');
    if (filter.status?.length) qs.set('status', filter.status.join(','));
    if (filter.open) qs.set('open', 'true');
    if (filter.overdue) qs.set('overdue', 'true');
    if (filter.due_today) qs.set('due_today', 'true');
    if (filter.limit) qs.set('limit', String(filter.limit));
    if (filter.offset) qs.set('offset', String(filter.offset));

    const s = qs.toString();
    const antwort = await fetchApi(`${API_BASE}${s ? `?${s}` : ''}`);
    return { items: antwort.items, total: antwort.total, limit: antwort.limit, offset: antwort.offset };
  },

  async anlegen(aufgabe: Partial<RecruitingTask>): Promise<RecruitingTask> {
    const antwort = await fetchApi(API_BASE, { method: 'POST', body: JSON.stringify(aufgabe) });
    return antwort.data;
  },

  async aendern(id: string, felder: Partial<RecruitingTask>): Promise<RecruitingTask> {
    const antwort = await fetchApi(`${API_BASE}/${id}`, { method: 'PATCH', body: JSON.stringify(felder) });
    return antwort.data;
  },

  async setzeStatus(id: string, status: TaskStatus): Promise<RecruitingTask> {
    const antwort = await fetchApi(`${API_BASE}/${id}/status`, {
      method: 'PATCH', body: JSON.stringify({ status }),
    });
    return antwort.data;
  },
};

export default recruitingTasksService;
